// Vendor Program v2 PR 1 — shared presentation maps for loan goals.

import type { StatusTone } from "@/design-system/components/ui";
import type {
  LoanGoalDto,
  LoanGoalPriority,
  LoanGoalStatus,
} from "@/api/portfolioAdmin/loanGoalsApi";
import { VENDOR_TYPE_OPTIONS } from "@/components/portfolioAdmin/vendors/VendorsListFilters";

export const GOAL_STATUS_BADGE: Record<
  LoanGoalStatus,
  { label: string; tone: StatusTone }
> = {
  proposed: { label: "Proposed", tone: "info" },
  active: { label: "Active", tone: "ok" },
  achieved: { label: "Achieved", tone: "onyx" },
  retired: { label: "Retired", tone: "muted" },
  discarded: { label: "Discarded", tone: "muted" },
};

export const GOAL_PRIORITY_BADGE: Record<
  LoanGoalPriority,
  { label: string; tone: StatusTone }
> = {
  high: { label: "High", tone: "warn" },
  standard: { label: "Standard", tone: "neutral" },
  low: { label: "Low", tone: "muted" },
};

/** Review-list tiering: High first, Low last (de-emphasized). */
export function priorityRank(priority: LoanGoalPriority): number {
  return priority === "high" ? 0 : priority === "standard" ? 1 : 2;
}

/** A vendor must never be buried in goals: assignment actions that push one
 *  vendor past this many OPEN (proposed + active — prospective load, not
 *  just published) goals on a loan show a non-blocking warning. 5 matches
 *  the report templates' top-5 improvement-objectives ceiling. Warning,
 *  never a block — the E1-VM's judgment governs. */
export const VENDOR_GOAL_SOFT_CAP = 5;

const TYPE_LABEL_BY_CODE = new Map(
  VENDOR_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);
const TYPE_RANK_BY_CODE = new Map(
  VENDOR_TYPE_OPTIONS.map((o, i) => [o.value, i]),
);
const UNKNOWN_RANK = VENDOR_TYPE_OPTIONS.length;
const OTHER_RANK = VENDOR_TYPE_OPTIONS.length + 1;

/** Display label for a suggested vendor type; null/unknown → "Other". */
export function vendorTypeLabel(code: string | null): string {
  if (!code) return "Other";
  return TYPE_LABEL_BY_CODE.get(code) ?? code;
}

/** Grouping key for the tab: the stored code "Other" and null are ONE group
 *  (both label "Other") — two cards with the same name would split the
 *  bulk-assign action. */
export function vendorTypeGroupKey(code: string | null): string | null {
  return code === "Other" ? null : code;
}

/** Catalog ordering for the tab's suggested-type groups ("Other"/null last,
 *  unknown legacy codes just before it). */
export function vendorTypeOrder(code: string | null): number {
  if (!code || code === "Other") return OTHER_RANK;
  return TYPE_RANK_BY_CODE.get(code) ?? UNKNOWN_RANK;
}

/** Not yet terminal — the goal still participates in work queues. */
export function isOpenGoal(goal: LoanGoalDto): boolean {
  return goal.status === "proposed" || goal.status === "active";
}

/** Open AND carried by no vendor — the "can't be quietly forgotten" set:
 *  drives the pinned banner, the per-group bulk-assign button, AND the bulk
 *  payload, so the three can never disagree. */
export function isUnassignedOpenGoal(goal: LoanGoalDto): boolean {
  return !goal.assignedPartyAssignmentId && isOpenGoal(goal);
}
